import {Trans} from '@lingui/macro';
import React, {FC, ReactNode} from 'react';
import styled from 'styled-components';

import {Handler, HandlerOf} from '../../../../libs/shared/helpers/src/react/reactHelpers';
import {Dropdown} from '../../../../libs/shared/ui/src/components/dropdowns/dropdown';
import {DropdownItem} from '../../../../libs/shared/ui/src/components/dropdowns/dropdownItem';
import {DropdownItemContent} from '../../../../libs/shared/ui/src/components/dropdowns/dropdownItemContent';
import {DropdownList} from '../../../../libs/shared/ui/src/components/dropdowns/dropdownList';
import {Icon} from '../../../../libs/shared/ui/src/components/icon/icon';
import {PopoverCoordinator} from '../../../../libs/shared/ui/src/components/popovers/popoverCoordinator';
import {useUserTheme} from '../../../../libs/shared/ui/src/theme/userThemeProvider';
import {useMeasureElement} from '../../../helpers/hooks/domHookHelpers';
import {
  createFormFieldHoverStyle,
  createFormInputFieldHeight,
} from '../../accounts/createAccount/createAccountStyles';
import {CreateFormInputDisplay} from './createFormInputDisplay';
import {addCreateFormInputDisabledStyles} from './createFormInputHelpers';

/*
 * Constants.
 */

const trueString = 'true';
const falseString = 'false';
const dropdownMinWidth = 140;

/*
 * Props.
 */

interface BooleanPopoverProps {
  title: ReactNode;
  value: string;
  onSelect: HandlerOf<string>;
  onDelete?: Handler;
  isModalInitiallyOpen?: boolean;
  shouldShowDeleteOptionInDropdown?: boolean;
  isDisabled?: boolean;
}

/*
 * Style.
 */

interface StyledButtonDivProps {
  $isDisabled?: boolean;
}
const StyledButtonDiv = styled.div<StyledButtonDivProps>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  background: ${(p) => p.theme.greys.shade20};
  border-radius: 8px;
  padding: 5px 8px;
  width: fit-content;
  height: ${createFormInputFieldHeight}px;

  ${createFormFieldHoverStyle};
  ${(p) => addCreateFormInputDisabledStyles(p.$isDisabled)}
`;

const StyledPlaceholderTextSpan = styled.span`
  color: ${(p) => p.theme.greys.shade60};
  white-space: nowrap;
`;

const StyledValueTextSpan = styled.span`
  color: ${(p) => p.theme.greys.shade90};
  white-space: nowrap;
`;

const StyledButtonContentDiv = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

/*
 * Component.
 */

export const BooleanPopover: FC<BooleanPopoverProps> = (props) => {
  const theme = useUserTheme();
  const {
    title,
    value,
    shouldShowDeleteOptionInDropdown,
    onSelect,
    onDelete,
    isModalInitiallyOpen,
    isDisabled,
  } = props;

  const [popoverButtonRef, {width: popoverButtonWidth}] = useMeasureElement();

  return (
    <CreateFormInputDisplay
      title={title}
      input={
        <PopoverCoordinator
          isDisabled={isDisabled}
          renderButton={() => (
            <StyledButtonDiv ref={popoverButtonRef} $isDisabled={isDisabled}>
              <StyledButtonContentDiv>
                <Icon name="togglesSmall" color={theme.greys.shade60} size={16} />
                {renderValueOrPlaceholder(value)}
              </StyledButtonContentDiv>
              <Icon name="chevronDownSmall" color={theme.greys.shade60} />
            </StyledButtonDiv>
          )}
          renderModal={(popoverProps) => (
            <Dropdown {...popoverProps} width={Math.max(dropdownMinWidth, popoverButtonWidth)}>
              <DropdownList>
                {maybeRenderNoneOption(value === '', shouldShowDeleteOptionInDropdown, () => {
                  onSelect('');
                  popoverProps.onRequestClose();
                })}
                <DropdownItem
                  onSelect={() => {
                    onSelect(trueString);
                    popoverProps.onRequestClose();
                  }}
                  isChecked={isTrue(value)}
                >
                  <DropdownItemContent>{renderValueOrPlaceholder(trueString)}</DropdownItemContent>
                </DropdownItem>
                <DropdownItem
                  onSelect={() => {
                    onSelect(falseString);
                    popoverProps.onRequestClose();
                  }}
                  isChecked={isFalse(value)}
                >
                  <DropdownItemContent>{renderValueOrPlaceholder(falseString)}</DropdownItemContent>
                </DropdownItem>
              </DropdownList>
            </Dropdown>
          )}
          isModalInitiallyOpen={Boolean(isModalInitiallyOpen)}
        />
      }
      onDelete={shouldShowDeleteOptionInDropdown ? undefined : onDelete}
    />
  );
};

/*
 * Helpers.
 */

function maybeRenderNoneOption(
  isChecked: boolean,
  shouldShowDeleteOptionInDropdown: boolean | undefined,
  onSelect: Handler,
) {
  if (!shouldShowDeleteOptionInDropdown) {
    return null;
  }
  return (
    <DropdownItem isChecked={isChecked} onSelect={onSelect}>
      <DropdownItemContent>--</DropdownItemContent>
    </DropdownItem>
  );
}

function renderValueOrPlaceholder(value: string) {
  if (isTrue(value)) {
    return (
      <StyledValueTextSpan>
        <Trans id="create-form-boolean-popover-value-yes">Yes</Trans>
      </StyledValueTextSpan>
    );
  }

  if (isFalse(value)) {
    return (
      <StyledValueTextSpan>
        <Trans id="create-form-boolean-popover-value-no">No</Trans>
      </StyledValueTextSpan>
    );
  }

  return (
    <StyledPlaceholderTextSpan>
      <Trans id="create-form-boolean-popover-select-value">Select value</Trans>
    </StyledPlaceholderTextSpan>
  );
}

function isTrue(value: string) {
  return value === trueString;
}

function isFalse(value: string) {
  return value === falseString;
}
